import { Vector3 } from '../engine/types/Vector3'
import { Quaternion } from '../engine/types/Quaternion'

export class PhaseUtils {
  // ==================== BASIC PHASE CALCULATION ====================

  /**
   * Calculate phase for 1D field (Line, Circle, Spiral)
   */
  static computePhase1D(index: number, totalCount: number): number {
    if (totalCount <= 1) return 0
    return (index / totalCount) * Math.PI * 2
  }

  /**
   * Calculate phase for 2D field (Grid, Hex) - diagonal progression
   */
  static computePhase2D(row: number, col: number, maxDimension: number): number {
    if (maxDimension <= 1) return 0
    return ((row + col) / (maxDimension * 2)) * Math.PI * 2
  }

  /**
   * Calculate radial phase for 2D field - ripple from center
   */
  static computeRadialPhase2D(
    row: number,
    col: number,
    centerRow: number,
    centerCol: number,
    maxDistance: number
  ): number {
    const distance = Math.sqrt(
      Math.pow(row - centerRow, 2) + Math.pow(col - centerCol, 2)
    )
    return (distance / maxDistance) * Math.PI * 2
  }

  // ==================== WAVE FUNCTIONS ====================

  /**
   * Apply sine wave with amplitude, frequency, time, and phase
   */
  static applyWave(
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): number {
    return amplitude * Math.sin(frequency * time + phase)
  }

  /**
   * Apply cosine wave
   */
  static applyCosWave(
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): number {
    return amplitude * Math.cos(frequency * time + phase)
  }

  /**
   * Apply smoothed wave (using smoothstep)
   */
  static applySmoothWave(
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): number {
    const rawWave = Math.sin(frequency * time + phase)
    const normalized = (rawWave + 1) / 2 // Convert -1,1 to 0,1
    const smoothed = this.smoothstep(0, 1, normalized)
    return amplitude * (smoothed * 2 - 1) // Convert back to -1,1
  }

  // ==================== DUAL PHASE SYSTEM ====================

  /**
   * Calculate dual phases for hover and rotation
   */
  static computeDualPhases(
    index: number,
    totalCount: number,
    hoverPhaseShift: number = 0,
    rotatePhaseShift: number = 0
  ): {
    hoverPhase: number
    rotatePhase: number
  } {
    const basePhase = this.computePhase1D(index, totalCount)
    
    return {
      hoverPhase: basePhase + hoverPhaseShift,
      rotatePhase: basePhase + rotatePhaseShift
    }
  }

  /**
   * Calculate dual phases for 2D fields
   */
  static computeDualPhases2D(
    row: number,
    col: number,
    maxDimension: number,
    hoverPhaseShift: number = 0,
    rotatePhaseShift: number = 0
  ): {
    hoverPhase: number
    rotatePhase: number
  } {
    const basePhase = this.computePhase2D(row, col, maxDimension)
    
    return {
      hoverPhase: basePhase + hoverPhaseShift,
      rotatePhase: basePhase + rotatePhaseShift
    }
  }

  // ==================== HOVER ANIMATIONS ====================

  /**
   * Calculate hover offset in transversal direction (perpendicular to field)
   */
  static calculateTransversalHover(
    normalVector: Vector3,
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): Vector3 {
    const waveValue = this.applyWave(amplitude, frequency, time, phase)
    return normalVector.clone().multiplyScalar(waveValue)
  }

  /**
   * Calculate hover offset in longitudinal direction (along field)
   */
  static calculateLongitudinalHover(
    tangentVector: Vector3,
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): Vector3 {
    const waveValue = this.applyWave(amplitude, frequency, time, phase)
    return tangentVector.clone().multiplyScalar(waveValue)
  }

  // ==================== ROTATION ANIMATIONS ====================

  /**
   * Calculate rotation offset around axis
   */
  static calculateRotationOffset(
    axis: Vector3,
    amplitude: number,
    frequency: number,
    time: number,
    phase: number
  ): Quaternion {
    const angle = this.applyWave(amplitude, frequency, time, phase)
    const quaternion = new Quaternion()
    quaternion.setFromAxisAngle(axis.clone().normalize(), angle)
    return quaternion
  }

  // ==================== ADVANCED WAVE EFFECTS ====================

  /**
   * Create breathing effect (synchronized expansion/contraction)
   */
  static createBreathingEffect(
    amplitude: number,
    frequency: number,
    time: number,
    synchronizedPhase: number = 0
  ): number {
    return this.applyWave(amplitude, frequency, time, synchronizedPhase)
  }

  /**
   * Create wind effect (progressive wave through field)
   */
  static createWindEffect(
    index: number,
    totalCount: number,
    amplitude: number,
    frequency: number,
    time: number,
    windSpeed: number = 1
  ): number {
    const phase = this.computePhase1D(index, totalCount) * windSpeed
    return this.applyWave(amplitude, frequency, time, phase)
  }

  /**
   * Create ripple effect (wave spreading from center)
   */
  static createRippleEffect(
    distanceFromCenter: number,
    maxDistance: number,
    amplitude: number,
    frequency: number,
    time: number,
    waveSpeed: number = 1
  ): number {
    const phase = (distanceFromCenter / maxDistance) * Math.PI * 2 * waveSpeed
    return this.applyWave(amplitude, frequency, time, phase)
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Smoothstep function for smooth transitions
   */
  static smoothstep(min: number, max: number, value: number): number {
    const t = Math.max(0, Math.min(1, (value - min) / (max - min)))
    return t * t * (3 - 2 * t)
  }

  /**
   * Normalize phase to 0-2π range
   */
  static normalizePhase(phase: number): number {
    return ((phase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)
  }

  /**
   * Lerp between two phases (handles wrap-around)
   */
  static lerpPhase(phase1: number, phase2: number, t: number): number {
    // Handle wrap-around for phase interpolation
    const diff = phase2 - phase1
    const adjustedDiff = diff > Math.PI ? diff - Math.PI * 2 : diff < -Math.PI ? diff + Math.PI * 2 : diff
    return phase1 + adjustedDiff * t
  }

  /**
   * Apply multiple wave functions for complex effects
   */
  static applyMultiWave(
    amplitude: number,
    frequencies: number[],
    weights: number[],
    time: number,
    phase: number
  ): number {
    let result = 0
    for (let i = 0; i < frequencies.length; i++) {
      const weight = weights[i] || 1
      result += weight * this.applyWave(amplitude, frequencies[i], time, phase)
    }
    return result / frequencies.length
  }

  /**
   * Create organic phase shift using multiple frequencies
   */
  static createOrganicPhaseShift(
    baseFrequency: number,
    time: number,
    variation: number = 0.3
  ): number {
    const primary = Math.sin(baseFrequency * time)
    const secondary = Math.sin(baseFrequency * time * 1.618) * 0.5 // Golden ratio
    const tertiary = Math.sin(baseFrequency * time * 2.414) * 0.25 // √2 * √3
    
    return (primary + secondary + tertiary) * variation
  }
} 